import { AppConfig } from '@dh-io-emh/config-types';
import { createHttpService } from '../utils/http.util';
import { Logger } from '../utils/logger.util';
import Vehicle, { ExtendedSMSProduct } from '../models/vehicle.model';

const VEHICLE_COUNT = 6;

export type DeepLinkingApi = {
  getSimilarSearchResults: (params: SimilarSearchResultsParams) => Promise<SimilarVehiclesResponse>;
  getSimilarProductsUrl: (productCode: string) => Promise<string>;
  getSimilarProducts: (code: string) => Promise<SimilarVehiclesResponse>;
};

export type DeepLinkingConfig = Pick<AppConfig, 'deepLinking' | 'lang' | 'shopCountry'>;

export type ProductDetailsResponse = {
  productCode: string;
  url: string;
  alternativeSearchUrl: string;
};

export type SimilarVehiclesResponse = {
  totalNumberOfResults: number;
  url: string;
  firstVehicles: ExtendedSMSProduct[];
  products: Vehicle[];
};

export type SimilarSearchResultsParams = {
  vehicleAssortment?: string;
  campaign?: string;
  bodyType?: string[];
  model?: string[];
  brand?: string[];
  color_group?: string[];
  fuel_type?: string;
  equipmentGroup?: string[];
  upholstery_polish?: string[];
  upholstery_group?: string[];
};

/**
 * Creates a deep linking api instance, which is used to get product details
 * and similar vehicles.
 *
 * Please see the following link for more information:
 * https://developer.corpinter.net/apis/deeplink_service?preselectedVersion=v1&preselectedEnvironment=production
 *
 * @param config {AppConfig} - app config
 * @returns {DeepLinkingApi}
 */
export const createDeepLinkingApi = ({ config }: { config: DeepLinkingConfig }): DeepLinkingApi => {
  const { deepLinking, lang, shopCountry: market } = config;
  const { apiKey, baseUrl } = deepLinking;

  /**
   * Get product details url for given product code.
   *
   * @param productCode {string} - product code
   * @returns {Promise<string>} - product details url
   */
  const getSimilarProductsUrl = async (productCode: string): Promise<string> => {
    const http = createHttpService({
      url: baseUrl,
      headers: {
        'x-api-key': apiKey,
      },
    });

    try {
      const result = await http.get<ProductDetailsResponse>(`product`, {
        params: {
          productCode,
          lang,
          market,
        },
      });

      return result.data.alternativeSearchUrl;
    } catch (error) {
      Logger.error('DeepLinkingApi::getProductDetails() -> unexpected error', error);
      throw error;
    }
  };

  /**
   * Get similar search results for given parameters.
   *
   * @param params {SimilarSearchResultsParams} - params
   * @returns {Promise<SimilarVehiclesResponse>} - similar products
   */
  const getSimilarSearchResults = async (params: SimilarSearchResultsParams) => {
    const http = createHttpService({
      url: baseUrl,
      headers: {
        'x-api-key': apiKey,
      },
    });

    try {
      const results = await http.get<SimilarVehiclesResponse>(`search/alternativeProductSearch`, {
        params: {
          emhlang: lang,
          emhmarket: market,
          ...Object.entries(params).reduce((acc, [key, val]) => ({ ...acc, [`emh${key}`]: val }), {}),
        },
      });

      // Create vehicle models from response data, which is needed for
      // other components, as it contains additional information.
      const products = results.data.firstVehicles.map((vehicle) => Vehicle.create(vehicle));

      return {
        ...results.data,
        products,
      };
    } catch (error) {
      Logger.error('DeepLinkingApi::getSimilarSearchResults() -> unexpected error', error);
      throw error;
    }
  };

  /**
   * Get similar products for given product code.
   *
   * @param code {string} - product code
   * @returns {Promise<SimilarVehiclesResponse>} - similar products
   */
  const getSimilarProducts = async (code: string) => {
    const http = createHttpService({
      url: '',
      headers: {
        'x-api-key': apiKey,
      },
    });

    try {
      const productDetailsUrl = await getSimilarProductsUrl(code);
      const searchUrl = productDetailsUrl.replace('/redirect/', '/search/');

      const results = await http.get<SimilarVehiclesResponse>(searchUrl, {
        params: {
          emhnumberOfVehiclesToReturn: VEHICLE_COUNT,
        },
      });

      // Create vehicle models from response data, which is needed for
      // other components, as it contains additional information.
      const products = results.data.firstVehicles.map((vehicle) => Vehicle.create(vehicle));

      return {
        ...results.data,
        products,
      };
    } catch (error) {
      Logger.error('DeepLinkingApi::getSimilarProducts() -> unexpected error', error);
      throw error;
    }
  };

  return {
    getSimilarSearchResults,
    getSimilarProductsUrl,
    getSimilarProducts,
  };
};
